import { makeAutoObservable } from 'mobx'
import * as R from 'ramda'
import i18n from 'i18next'

export default class PPQuestion {
    questionId = ''

    pageQuestionId = ''

    kpiQuestionId = ''

    kpiQuestionShortText = ''

    kpiQuestionLongText = ''

    questionLongText = ''

    questionShortText = ''

    questionTypeCode = ''

    kpiType = ''

    surveyId = ''

    surveyDefaultLocale = ''

    year = 0

    respondentType = ''

    chart = ''

    benchmark = []

    comparison = []

    comparisonYears = []

    questions = []

    highlightedOptions = {}

    filter = {
        state: [
            'completed',
        ],
        surveyDataIds: [],
        question: {
            ruleType: 'ruleGroup',
            rules: [],
        },
        campaign: {
            ruleType: 'ruleGroup',
            rules: [],
        },
        responseDate: {
            ruleType: 'ruleGroup',
            rules: [],
        },
    }

    hasNonNumericValues = false

    hasOtherOrExclusive = false

    hasNumericCodes = false

    settings = {
        sorting: 'noRanking',
        removeZero: false,
        removeBaseLimit: 0,
        excludeNonNumeric: false,
        locked: false,
        showAverage: false,
        optionsDisplayed: null,
    }

    questionTypeCode = ''

    questionCoding = {
        labels: [],
        categories: [],
    }

    chosenCategories = new Map()

    isLoadingTopResponses = false

    isRateLimited = false

    position = 0

    pendingChanges = {
        category: '',
        labels: [],
        pct: 0,
    }

    constructor(props) {
        this.questionId = props.questionId ?? ''
        this.pageQuestionId = props.pageQuestionId ?? ''
        this.questionLongText = props.questionLongText ?? ''
        this.questionShortText = props.questionShortText ?? ''
        this.kpiQuestionId = props.kpiQuestionId ?? ''
        this.kpiQuestionShortText = props.kpiQuestionShortText ?? ''
        this.kpiQuestionLongText = props.kpiQuestionLongText ?? ''
        this.surveyId = props.surveyId ?? ''
        this.respondentType = props.respondentType ?? ''
        this.chart = props.chart ?? ''
        this.benchmark = props.benchmark?.slice() ?? []
        this.comparison = props.comparison?.slice() ?? []
        this.comparisonYears = props.comparisonYears ?? []
        this.highlightedOptions = props.highlightedOptions ?? {}
        this.hasOtherOrExclusive = props.hasOtherOrExclusive ?? false
        this.hasNonNumericValues = props.hasNonNumericValues ?? false
        this.hasNumericCodes = props.hasNumericCodes ?? false
        this.filter = props.filter ?? null
        this.questions = props.questions ?? []
        this.settings = props.settings ?? {
            sorting: 'noRanking',
            removeZero: false,
            removeBaseLimit: 0,
            excludeNonNumeric: false,
            showAverage: props.questionTypeCode === 'OD',
            locked: false,
            optionsDisplayed: null,
            showPromoterDetractor: false,
        }
        this.questionTypeCode = props.questionTypeCode ?? ''
        this.position = props.position ?? 0
        this.year = props.year ?? 0
        this.ppStore = props.ppStore
        this.store = props.store
        this.kpiType = props.kpiType
        // Capture the default locale of the survey this question was added from so
        // option labels can be rendered in the survey's language regardless of the
        // viewer's UI locale. Prefer the persisted value (rehydrated saves); fall
        // back to resolving it from the survey by id at add time.
        this.surveyDefaultLocale = props.surveyDefaultLocale
            ?? props.ppStore?.getSurveyDefaultLocale?.(this.surveyId)
            ?? ''
        // Rehydrate verbatim picks from saved/snapshot data. Accepts both the
        // current persisted shape ({ position, name, comments, pct }) and the
        // legacy shape ({ name, comments, pct }) by falling back to the array
        // index as the slot position. Also tolerates either field-name pair.
        if (Array.isArray(props.questionCoding)) {
            props.questionCoding.forEach((entry, index) => {
                const position = entry.position ?? index
                const category = entry.category ?? entry.name ?? ''
                const labels = entry.labels ?? entry.comments ?? []
                const pct = entry.pct ?? 0
                this.chosenCategories.set(position, { category, labels, pct })
            })
        }
        makeAutoObservable(this)

        // if (['TA', 'TX'].includes(this.questionTypeCode)) {
        //     this.fetchData()
        // }
    }

    toJSON = () => ({
        questionId: this.questionId,
        pageQuestionId: this.pageQuestionId,
        questionLongText: this.questionLongText,
        questionShortText: this.questionShortText,
        kpiQuestionId: this.kpiQuestionId,
        kpiQuestionShortText: this.kpiQuestionShortText,
        kpiQuestionLongText: this.kpiQuestionLongText,
        surveyId: this.surveyId,
        surveyDefaultLocale: this.surveyDefaultLocale,
        respondentType: this.respondentType,
        chart: this.chart,
        kpiType: this.kpiType,
        hasNonNumericValues: this.hasNonNumericValues,
        hasNumericCodes: this.hasNumericCodes,
        hasOtherOrExclusive: this.hasOtherOrExclusive,
        highlightedOptions: this.highlightedOptions,
        benchmark: (['pie', 'doughnut'].includes(this.chart) || this.questionTypeCode === 'OD') ? [] : this.benchmark.slice(),
        comparison: (['pie', 'doughnut'].includes(this.chart) || this.questionTypeCode === 'OD') ? [] : this.comparison.slice(),
        comparisonYears: (['pie', 'doughnut'].includes(this.chart) || this.questionTypeCode === 'OD') ? [] : this.comparisonYears.slice(),
        filter: this.filter,
        settings: this.settings,
        questionTypeCode: this.questionTypeCode,
        questionCoding: Array.from(this.chosenCategories.entries()).map(([position, { category, labels, pct }]) => ({
            position, name: category, comments: labels, pct,
        })),
        position: this.position,
        questions: this.questions,
        year: this.year,
    })

    get codedCategories() {
        return this.questionCoding.categories.filter((c) => c.pct !== 0)
    }

    get codedLabels() {
        return this.questionCoding.labels
    }

    get categoryAtCurrentPosition() {
        return this.pendingChanges.category
    }

    get labelsForCategory() {
        return this.codedLabels.filter((cl) => cl.coding.has(this.categoryAtCurrentPosition))
    }

    get hasPickedComments() {
        return [...this.chosenCategories.values()].some(({ labels }) => labels?.length > 0)
    }

    get isVerbatim() {
        return ['TA', 'TX'].includes(this.questionTypeCode)
    }

    get highlighted() {
        return Object.entries(this.highlightedOptions).flatMap(([series, indexes]) => (
            (Array.isArray(indexes) ? indexes : [indexes]).map((index) => ({ series, index }))
        ))
    }

    fetchData = async () => {
        const { store } = this
        const request = JSON.stringify({
            questionId: this.questionId,
            questionType: this.questionTypeCode,
            questionOptionId: null,
            filter: {
                state: [],
                surveyDataIds: [],
                question: {
                    ruleType: 'ruleGroup',
                    rules: [],
                },
                campaign: {
                    ruleType: 'ruleGroup',
                    rules: [],
                },
                responseDate: {
                    ruleType: 'ruleGroup',
                    rules: [],
                },
            },
        })

        const response = await store.http.post(`/report/v1/${this.surveyId}/textquestion`, request)

        const questionCoding = {}

        const allCategories = [...new Set(response.categories.flat())]
        console.log('all labels', allCategories)
        const labels = response.labels.map((label, index) => {
            const coding = R.pathOr([], ['categories', index], response)

            coding.forEach((category) => {
                questionCoding[category] ??= 0
                questionCoding[category] += 1
            })

            return {
                label,
                coding: new Set(coding),
            }
        })
        const otherKey = 'Misc/Other'
        const other = R.pick([otherKey], questionCoding)
        const total = Object.entries(questionCoding).reduce((a, [_, v]) => a + v, 0)
        const sortedQuestionCoding = Object.entries(R.omit([otherKey], questionCoding))
            .sort((a, b) => b[1] - a[1])
            .map(([k, v]) => {
                const pct = (v / (total ?? 1) * 100)
                return { label: `${k} (${Math.round(pct)})`, value: k, pct }
            })
        this.questionCoding = {
            labels,
            categories: [...sortedQuestionCoding, { label: otherKey, value: otherKey, pct: (other[otherKey] ?? 0 / (total ?? 1) * 100) }],
        }
    }

    generateCoding = async () => {
        const { store } = this

        try {
            await store.http.post(`/report/v1/${this.surveyId}/textquestion/${this.questionId}/code`, JSON.stringify({}))
            await this.fetchData()
        } catch (e) {
            console.error(e)
            store.addNotification(null, 'Error loading question coding', 'error')
        }
    }

    handleAutoFillVerbatimSlots = async () => {
        const { store, surveyId, questionId } = this
        this.isLoadingTopResponses = true
        this.isRateLimited = false
        store.addNotification(null, i18n.t('powerpoint.modals.verbatim.autofill_generating'), 'success')

        try {
            const response = await store.http.post(
                `/report/v1/${surveyId}/textquestion/${questionId}/topresponses`,
                JSON.stringify({}),
            )

            response.forEach(({ category, comments }, index) => {
                const pct = this.questionCoding.categories.find((c) => c.value === category)?.pct ?? 0
                this.chosenCategories.set(index, { category, labels: comments, pct })
            })

            this.ppStore.scheduleSave()
            this.ppStore.handleCloseVerbatimSettingsModal()
        } catch (e) {
            if (e?.message?.message === 'rate_limited') {
                this.isRateLimited = true
                store.addNotification(null, i18n.t('report.verbatim.aiCoding.rateLimit.rate_limited'), 'error')
            } else {
                console.error(e)
                store.addNotification(null, 'Error picking top responses', 'error')
            }
        } finally {
            this.isLoadingTopResponses = false
        }
    }

    handleChooseCategory = (category, pct) => {
        this.pendingChanges.category = category
        this.pendingChanges.pct = pct
        this.pendingChanges.labels = []
    }

    handleChooseLabel = (label) => {
        if (this.pendingChanges.labels.includes(label)) {
            this.pendingChanges.labels = this.pendingChanges.labels.filter((l) => l !== label)
        } else if (this.pendingChanges.labels.length === this.ppStore.verbatimSlots) {
            this.pendingChanges.labels.shift()
            this.pendingChanges.labels.push(label)
        } else {
            this.pendingChanges.labels.push(label)
        }
    }

    handleSaveVerbatimChoices = () => {
        this.chosenCategories.set(this.ppStore.verbatimPosition, this.pendingChanges)
        this.pendingChanges = {
            category: '',
            labels: [],
            pct: 0,
        }
        this.ppStore.handleCloseVerbatimSettingsModal()
    }

    setHighlightedOptions = (options) => {
        this.highlightedOptions = options
    }

    handleClearVerbatimSelections = () => {
        this.pendingChanges.labels = []
    }

    handleCancelVerbatimChoices = () => {
        this.pendingChanges = {
            category: '',
            labels: [],
            pct: 0,
        }
        this.ppStore.handleCloseVerbatimSettingsModal()
    }

    handleCategoryToggle = (e) => {
        this.chosenCategories.push(e.name)
    }

    updateSettings = (event) => {
        this.settings[event.name] = event.value
    }

    updateChart = (event) => {
        if (['npsChart', 'kpiChart'].includes(event.value)) {
            this.settings.showAverage = false
        }
        this.chart = event.value
    }

    addComparison = (surveyId, year) => {
        if (this.comparison.includes(surveyId)) return
        this.comparison.push(surveyId)
        this.comparisonYears.push(year)
    }

    removeComparison = (surveyId, year) => {
        this.comparison = this.comparison.filter((c) => c !== surveyId)
        this.comparisonYears = this.comparisonYears.filter((y) => y !== year)
    }

    addBenchmark = (benchmark) => {
        if (this.benchmark.includes(benchmark)) return
        this.benchmark.push(benchmark)
    }

    removeBenchmark = (benchmark) => {
        this.benchmark = this.benchmark.filter((ben) => benchmark !== ben)
    }
}
